import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, EmptyState, ErrorState, LoadingState } from '../ui';
import {
    coverageState, coverageTotals, findPhotoIssues, findUnlistedUnits, groupPhotoIssuesByBranch,
    groupUnlistedByBranch, unitValue, unlistedForBranch,
} from '../../utils/listingCoverage';
import { inventoryLink } from '../../utils/branchView';
import { sellSpeedCategory } from '../../utils/sellSpeedCategory';
import { VERDICT_CONFIG } from '../../utils/anomalySignals';
import InventoryLink from './InventoryLink';
import { daysInStockSeverity } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';

const speedTone = (cat) => {
    if (!cat) return null;
    if (cat.startsWith('Fast')) return 'ok';
    if (cat.startsWith('Normal')) return 'neutral';
    if (cat.startsWith('Very Slow')) return 'crit';
    if (cat.startsWith('Slow')) return 'warn';
    return null;
};
const POSITIVE = ['Outperformer', 'High Interest'];
const fullMoney = (v) => `$${Math.round(v).toLocaleString('en-US')}`;

export const NOT_IN_STOCK_TIP = "Listed on the marketplace, but not in this dealership's current stock (sold, new or not yet received)";

/**
 * The card's way into the unit: a click anywhere on the card opens it, and
 * the #stock button is the keyboard way in (Enter / Space, focus ring on the
 * card). The button stops its own click so one press opens the unit once.
 */
const openProps = (open) => ({ onClick: open });
const StockButton = ({ stock, onOpen }) => (
    <button type="button" title="Open unit detail" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
        #{stock}
    </button>
);
StockButton.propTypes = { stock: PropTypes.node.isRequired, onOpen: PropTypes.func.isRequired };

/** Unlisted units of a branch by predicted sell speed (P-BR-082). */
function SpeedBar({ row }) {
    const seg = (tone, v) => (v > 0 ? <span className={`v2-tone-${tone}`} style={{ width: `${(v / row.total) * 100}%` }} /> : null);
    return (
        <>
            <span className="br2-mini br2-mini--wide" aria-hidden="true" data-testid="speed-bar">
                {seg('sage', row.fast)}{seg('slate', row.normal)}{seg('amber', row.slow)}{seg('red', row.verySlow)}
            </span>
            <span className="v2-sr">{`Sell speed: ${row.fast} fast, ${row.normal} normal, ${row.slow} slow, ${row.verySlow} very slow.`}</span>
        </>
    );
}

SpeedBar.propTypes = { row: PropTypes.object.isRequired };

/**
 * Listing coverage (P-BR-072..097): units in stock with no listing, and
 * listings with fewer photos than the same make / model / year. The tiles
 * switch between the two views; each view remembers its own branch. Every
 * unit of the branch is shown (no cap); the stock number opens today's unit
 * pane. A failed /pricing-matrix is an error with Retry, never "every unit
 * not listed".
 */
export default function ListingCoverageV2({ data, onOpenUnit }) {
    // Days in stock: red past the last aging boundary, amber past the critical days.
    const { aging } = useDealershipSettings();
    const {
        invUnits, inventoryLoaded, inventoryLoading, inventoryError, matrixData, matrixLoading, matrixError,
        retryMatrix, retryInventory, combinedData,
    } = data;
    const [view, setView] = useState('unlisted');
    const [unlistedBranch, setUnlistedBranch] = useState(null);
    const [photoBranch, setPhotoBranch] = useState(null);

    const unlisted = useMemo(() => findUnlistedUnits(invUnits, matrixData?.rows), [invUnits, matrixData]);
    const photoRows = useMemo(() => findPhotoIssues(matrixData?.rows), [matrixData]);
    const unlistedRows = useMemo(() => groupUnlistedByBranch(unlisted), [unlisted]);
    const photoBranchRows = useMemo(() => groupPhotoIssuesByBranch(photoRows), [photoRows]);
    const totals = coverageTotals(unlisted);
    const allNames = (combinedData?.branches || []).map((b) => b.branch_name);

    const status = coverageState({
        inventoryLoaded, inventoryLoading, inventoryError, units: invUnits, matrixData, matrixLoading, matrixError,
    });
    if (status === 'matrixError') {
        return <ErrorState title="Listing data could not be loaded" message={matrixError} onRetry={retryMatrix} />;
    }
    if (status === 'inventoryError') return <ErrorState message={inventoryError} onRetry={retryInventory} />;
    if (status === 'loading') return <LoadingState label="Loading coverage data..." />;
    if (status === 'empty') return <EmptyState message="No units in stock." />;

    const isPhotos = view === 'photos';
    const rows = isPhotos ? photoBranchRows : unlistedRows;
    const active = (isPhotos ? photoBranch : unlistedBranch) || rows[0]?.branch;
    const setActive = isPhotos ? setPhotoBranch : setUnlistedBranch;
    const units = isPhotos ? photoRows.filter((r) => r.branch === active) : unlistedForBranch(unlisted, active);
    const max = Math.max(1, ...rows.map((r) => r.count));

    const tiles = [
        { id: 'unlisted', n: totals.count.toLocaleString('en-US'), l: 'Not Listed', p: 'in stock, not listed' },
        { id: 'unlisted', n: `$${(totals.value / 1_000_000).toFixed(1)}M`, l: 'Value Unlisted', p: 'asking price of those units' },
        { id: 'unlisted', n: totals.verySlow, l: 'Very Slow Unlisted', p: 'predicted 180+ days to sell', tone: totals.verySlow > 0 ? 'crit' : null },
        { id: 'photos', n: photoRows.length, l: 'Below-Avg Photos', p: 'vs same make, model, year', tone: photoRows.length > 0 ? 'warn' : null },
    ];

    return (
        <div className="br2-panel">
            <div className="br2-covk" role="group" aria-label="Coverage summary">
                {tiles.map((t) => (
                    <button key={t.l} type="button" aria-pressed={view === t.id} onClick={() => setView(t.id)} data-tile={t.l}>
                        <span className={`br2-covk-n${t.tone ? ` br2-ink-${t.tone}` : ''}`}>{t.n}</span>
                        <span className="br2-covk-l">{t.l}</span>
                        <span className="br2-covk-p">{t.p}</span>
                    </button>
                ))}
            </div>
            <div className="br2-covlay">
                <section className="v2-board br2-board" aria-labelledby="br2-covl-h">
                    <div className="br2-card-h">
                        <h2 id="br2-covl-h">{isPhotos ? 'Photo quality by branch' : 'Not listed by branch'}</h2>
                        <span className="br2-spacer" />
                        <span className="br2-sub">{isPhotos ? 'flagged units' : 'unlisted units'}</span>
                    </div>
                    <div className="br2-blist">
                        {rows.length === 0 && <p className="br2-bav-empty">{isPhotos ? 'No photo issues.' : 'Every unit is listed.'}</p>}
                        {rows.map((r) => (
                            <button key={r.branch} type="button" aria-pressed={r.branch === active} onClick={() => setActive(r.branch)} data-cov-branch={r.branch}>
                                <span className="br2-blist-top">
                                    <b>{r.branch}</b>
                                    <span>{r.count} {isPhotos ? 'flagged' : 'unlisted'}</span>
                                </span>
                                {isPhotos ? (
                                    <>
                                        <span className="br2-mini br2-mini--wide" aria-hidden="true"><span className="v2-tone-amber" style={{ width: `${(r.count / max) * 100}%` }} /></span>
                                        {r.noPhotos > 0 && <Chip tone="crit" className="br2-tag br2-tag--start">{r.noPhotos} no photos</Chip>}
                                    </>
                                ) : (
                                    <>
                                        <SpeedBar row={r} />
                                        {r.value > 0 && <span className="br2-blist-v">${(r.value / 1_000_000).toFixed(2)}M</span>}
                                    </>
                                )}
                            </button>
                        ))}
                    </div>
                </section>
                <section className="v2-board br2-board" aria-labelledby="br2-covd-h">
                    <div className="br2-card-h">
                        <h2 id="br2-covd-h">{active || '—'}</h2>
                        <span className="br2-sub" data-testid="cov-count">
                            {isPhotos ? `${units.length} below avg` : `${units.length} unlisted, oldest first`}
                        </span>
                        <span className="br2-spacer" />
                        {active && (
                            <InventoryLink to={inventoryLink({ names: [active], allNames })} className="v2-btn">
                                Open branch inventory <span aria-hidden="true">›</span>
                            </InventoryLink>
                        )}
                    </div>
                    {units.length === 0 ? (
                        <p className="br2-bav-empty">{isPhotos ? 'No photo issues for this branch.' : 'No unlisted units for this branch.'}</p>
                    ) : (
                        <ul className="br2-ucards">
                            {isPhotos ? units.map((r, i) => {
                                const sev = r.photo_count === 0 ? 'crit' : 'warn';
                                const diff = r.avg_photos != null ? Math.round(r.avg_photos - r.photo_count) : null;
                                // The listing data is not intersected with the current stock
                                // (sold, new or not yet received): such cards stay counted
                                // but have nothing to open.
                                const inv = r.stock_number ? invUnits.find((u) => String(u.StockNumber).trim() === String(r.stock_number).trim()) : null;
                                const open = inv ? () => onOpenUnit(inv) : null;
                                return (
                                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
                                    <li
                                        key={r.stock_number || i}
                                        className={`br2-ucard br2-ucard--${sev}${open ? ' br2-ucard--open' : ''}`}
                                        data-testid="unit-card"
                                        {...(open ? openProps(open) : {})}
                                    >
                                        <div className="br2-ucard-t">
                                            <b>{[r.year, r.make, r.model].filter(Boolean).join(' ')}</b>
                                            {r.stock_number && open && <StockButton stock={r.stock_number} onOpen={open} />}
                                            {r.stock_number && !open && <span className="br2-ucard-stock">#{r.stock_number}</span>}
                                        </div>
                                        {r.stock_number && !open && (
                                            <div className="br2-ucard-s">
                                                <Chip tone="none" className="br2-tag br2-tag--start" title={NOT_IN_STOCK_TIP}>Not in current inventory</Chip>
                                            </div>
                                        )}
                                        <div className="br2-ucard-m">
                                            <span>{r.is_new === true ? 'New' : 'Used'}</span>
                                            <span><b className={`br2-ink-${sev}`}>{r.photo_count === 0 ? 'No photos' : `${r.photo_count} photos`}</b></span>
                                            {r.avg_photos != null && <span>avg {r.avg_photos}{diff > 0 ? ` · needs +${diff}` : ''}</span>}
                                            <span><b>{fullMoney(r.price || 0)}</b></span>
                                        </div>
                                    </li>
                                );
                            }) : units.map((u) => {
                                const days = u.DaysInStock || 0;
                                const sev = daysInStockSeverity(days, aging);
                                const val = unitValue(u);
                                const speed = sellSpeedCategory(u);
                                const sTone = speedTone(speed);
                                // Today: only known verdicts, and not the positive ones.
                                const showVerdict = Boolean(VERDICT_CONFIG[u.AnomalyVerdict]) && !POSITIVE.includes(u.AnomalyVerdict);
                                return (
                                    // Unlisted cards come from the inventory itself, so their unit always exists.
                                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
                                    <li key={u.StockNumber} className={`br2-ucard br2-ucard--${sev} br2-ucard--open`} data-testid="unit-card" {...openProps(() => onOpenUnit(u))}>
                                        <div className="br2-ucard-t">
                                            <b>{[u.YEAR_MANUF, u.MAKE, u.MODEL].filter(Boolean).join(' ')}</b>
                                            <StockButton stock={u.StockNumber} onOpen={() => onOpenUnit(u)} />
                                        </div>
                                        <div className="br2-ucard-m">
                                            <span>{u.CATEGORY || '—'}</span>
                                            <span className={sev === 'ok' ? undefined : `br2-ink-${sev}`}><b className="br2-inherit">{days}d</b> in stock</span>
                                            {val > 0 && <span><b>{fullMoney(val)}</b></span>}
                                        </div>
                                        {(sTone || showVerdict) && (
                                            <div className="br2-ucard-s">
                                                {sTone && <Chip tone={sTone} className="br2-tag">{speed}</Chip>}
                                                {showVerdict && (
                                                    <Chip tone={VERDICT_CONFIG[u.AnomalyVerdict]?.severity === 'critical' ? 'crit' : 'warn'} className="br2-tag">
                                                        {u.AnomalyVerdict}
                                                    </Chip>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}

ListingCoverageV2.propTypes = {
    data: PropTypes.object.isRequired,
    onOpenUnit: PropTypes.func.isRequired,
};
